import { Injectable, NgZone } from '@angular/core';
import { Subject, Subscription, ReplaySubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Internal interfaces not to be exposed.
 */
interface IListener {
    weight: number;
    subject: Subject<any>;
}

interface IListenerWrapper {
    listeners: IListener[];
    subject: Subject<any>;
}

/**
 * GlobalEventBusService is a singleton service that can be injected to any other component or service.
 * The GlobalEventBusService supports 2 types of callbacks.
 * 1- Regular callbacks where the order of the events is not important to the listeners.
 * 2- PriorityQueue callbacks based on a provided weight.
 * Both types can listen to the same events and PriorityQueue callbacks are executed first in their priority order.
 *
 * Note:
 * The callbacks work in both Angularjs and Angular frameworks so you can listen to any event from any side.
 *
 * add the keys to GlobalEvents in src/app/ng-app/core/global-event-constants
 */
@Injectable({
    providedIn: 'root'
})
export class GlobalEventBusService {
    private weightedQueueChannels: { [key: string]: IListenerWrapper };

    constructor() {
        this.weightedQueueChannels = {};
    }

    public broadcast = <T>(keyEvent: string, data: T): void => {
        // When broadcasting attempt to initialize the channels.
        this.init<T>(keyEvent);

        this.weightedQueueChannels[keyEvent].subject.next(data);
    }

    /**
     * Register a callback with a specific event key.
     * The caller needs to provide a callback that takes as input the objected expected from the notification.
     * Duplicate callbacks are allowed cause each of the registrations will return a new Subscription object that can be used to unregister.
     *
    * If previousUpdate is set to true, the subscriber will be notified with the latest change before registration.
     * Important Note:
     * If an event was already broadcasted the callback will be called immediately during the register call.
     * This means you can't unsubsribe within the callback and it needs to happen after register call.
     * Check unit-tests for examples.
     *
     * If previousUpdate is set to false, the subscriber will only be notified with new changes.
     *
     * To unregister from listening for further events call unsubscribe() on the returned Subscription object
     */
    public register = <T>(eventKey: string, listener: (data: T) => void, previousUpdate: boolean = false): Subscription => {
        // Because we could register for events before broadcasting we should try to initialize if it was not initialized before.
        this.init<T>(eventKey);

        return this.registerWithWeight(eventKey, listener, previousUpdate);
    }

    /**
     * Register a callback with a specific event key using a priority queue with a specific weight value.
     * The weight value is optional and if not provided the listener will be added to the end of the priority queue.
     * In case same weight is used, the callback will be added at the end of similar weight group class.
     *
     * If previousUpdate is set to true, the subscriber will be notified with the latest change before registration.
     * Important Note:
     * If an event was already broadcasted the callback will be called immediately during the register call.
     * This means you can't unsubsribe within the callback and it needs to happen after register call.
     * Check unit-tests for examples.
     *
     * If previousUpdate is set to false, the subscriber will only be notified with new changes.
     *
     * To unregister from listening for further events call unsubscribe() on the returned Subscription object.
     */
    public registerWithWeight = <T>(eventKey: string, callback: (data: T) => void, previousUpdate: boolean = false, weight?: number): Subscription => {
        // Because we could register for events before broadcasting we should try to initialize if it was not initialized before.
        this.init<T>(eventKey);

        const subject = new ReplaySubject<T>(1);
        const result = subject.subscribe(callback);
        subject.pipe(
            finalize(() => {
                this.unregisterWithWeight(eventKey, subject);
            })
        );

        if (!weight) {
            this.weightedQueueChannels[eventKey].listeners.push({ weight: null, subject: subject });
        } else {
            let index = 0;
            for (const listener of this.weightedQueueChannels[eventKey].listeners) {
                if (!listener.weight || listener.weight < weight) {
                    break;
                }
                index++;
            }
            this.weightedQueueChannels[eventKey].listeners.splice(index, 0, { weight: weight, subject: subject });
        }

        if (previousUpdate) {
            // In case we had previous events triggered. We will subscribe to the main subject and unsubscribe.
            // If we have triggered an event before subscription, it will update the subject 1 time.
            // Future events will be notified through listeners array.
            const sub = this.weightedQueueChannels[eventKey].subject.subscribe((data) => subject.next(data));
            sub.unsubscribe();
        }

        return result;
    }

    /**
     * Stop listening to a specific event key using a specific callback.
     * The same subjed used in registerWithPriority must be used to unregister the event.
     *
     * If the subject is not registered, nothing will happen.
     */
    private unregisterWithWeight = <T>(eventKey: string, subject: Subject<T>): void => {
        if (!this.weightedQueueChannels[eventKey]) {
            return;
        }

        const index = this.findPriorityQueueIndex(eventKey, subject);
        if (index > -1) {
            this.weightedQueueChannels[eventKey].listeners.splice(index, 1);
        }
    }

    private findPriorityQueueIndex = <T>(eventKey: string, subject: Subject<T>): number => {
        // Find the index of the callback.
        let index = -1;
        for (const listener of this.weightedQueueChannels[eventKey].listeners) {
            index++;
            if (listener.subject === subject) {
                return index;
            }
        }

        // callback not found return -1
        return -1;
    }

    private init = <T> (eventKey: string): void => {
        if (!this.weightedQueueChannels[eventKey]) {
            this.weightedQueueChannels[eventKey] = { subject: new ReplaySubject<T>(1), listeners: [] };
            this.weightedQueueChannels[eventKey].subject.subscribe((data) => {
                for (const listener of this.weightedQueueChannels[eventKey].listeners) {
                    listener.subject.next(data);
                }
            });
        }
    }
}
